import { useEffect, useLayoutEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberOpeningRoutingVenues } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import TimedOpeningPanel from './TimedOpeningPanel';
import TimedIncrementPanel from './TimedIncrementPanel';
import TimedReplacementPanel, { type Settings } from './TimedReplacementPanel';
import { useCrmWorkflow } from './CrmWorkflowContext';
import {
  canLeaveCrm,
  readCrmRecovery,
  type CrmPurpose,
  type CrmRecoveryItem,
} from './crmWorkspaceState';
import { ALERT, NOTE, OUTLINE, PRIMARY } from './importStyles';

export default function CrmPointsWorkspace({ entryVenueId }: { entryVenueId: string }) {
  const { t } = useTranslation('member');
  const actor = useAuthStore((s) => s.user?.id ?? '');
  const session = useAuthStore((s) => s.sessionRevision);
  const workflow = useCrmWorkflow();
  const [purpose, setPurpose] = useState<CrmPurpose | null>(null);
  const [, setStorageRevision] = useState(0);
  const routing = useQuery({
    queryKey: ['opening-routing-venues', actor, session, entryVenueId],
    queryFn: () =>
      api.get<MemberOpeningRoutingVenues>('/members/import/timed-opening/routing-venues'),
    enabled: !!actor && !!entryVenueId,
    retry: false,
    staleTime: 30000,
  });
  const context =
    routing.data?.entryVenueId === entryVenueId &&
    typeof routing.data.environment === 'string' &&
    routing.data.environment.trim()
      ? routing.data
      : null;
  const settings = useQuery({
    queryKey: ['crm-replacement-settings', actor, entryVenueId, session],
    queryFn: () => api.get<Settings>('/members/import/timed-replacement/settings'),
    enabled: !!context,
    retry: false,
    staleTime: 30000,
  });
  const contextReady = !!context && !!settings.data && !routing.isError && !settings.isError;
  if (workflow)
    workflow.guard.newWritesAllowed =
      contextReady && !(settings.data?.mode === 'REPLACEMENT' && purpose !== 'REPLACEMENT');
  let recovery: CrmRecoveryItem[] = [];
  if (context) recovery = readCrmRecovery(sessionStorage, actor, entryVenueId, context.environment);
  const unresolved = recovery.length > 0 || !!workflow?.model?.unresolved;
  const recoveryLocked = !context || unresolved;
  if (workflow) workflow.guard.unresolved = recoveryLocked;
  useLayoutEffect(() => {
    workflow?.refresh();
  }, [recoveryLocked]);
  useEffect(() => {
    const changed = () => setStorageRevision((n) => n + 1);
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, []);
  const select = (next: CrmPurpose) => {
    if (!workflow || !canLeaveCrm(workflow.guard, () => window.confirm(t('crmWorkspace.discard'))))
      return;
    setPurpose(next);
    workflow.guard.dirty = false;
  };
  const phases = ['FILES', 'PREVIEW', 'CONFIRM', 'RESULT'] as const;
  const model = workflow?.model?.purpose === purpose ? workflow.model : null;
  return (
    <section
      className="space-y-4 min-w-0"
      aria-label={t('crmWorkspace.title')}
      onChangeCapture={() => {
        if (workflow) workflow.guard.dirty = true;
      }}
    >
      <p className={NOTE}>{t('crmWorkspace.scope')}</p>
      {(!context || settings.isPending) && <p role="status">{t('crmWorkspace.loading')}</p>}
      {(routing.isError || settings.isError || (routing.isSuccess && !context)) && (
        <p role="alert" className={ALERT}>
          {t('crmWorkspace.contextFailed')}
        </p>
      )}
      {recovery.length > 0 && (
        <section
          className="border border-warning p-4 space-y-2"
          aria-label={t('crmWorkspace.recovery')}
        >
          <h2 className="text-xl font-semibold">{t('crmWorkspace.recovery')}</h2>
          <p>{t('crmWorkspace.recoveryHint')}</p>
          {recovery.map((item, index) => (
            <button
              type="button"
              className={OUTLINE}
              key={`${item.purpose}:${index}`}
              disabled={workflow?.guard.busy || item.corrupt || purpose === item.purpose}
              onClick={() => {
                if (!workflow || workflow.guard.busy || purpose === item.purpose) return;
                if (workflow.guard.dirty && !window.confirm(t('crmWorkspace.discard'))) return;
                workflow.guard.dirty = false;
                setPurpose(item.purpose);
              }}
            >
              {t(`crmWorkspace.${item.purpose}`)} —{' '}
              {t(item.corrupt ? 'crmWorkspace.corrupt' : 'crmWorkspace.lookup')}
            </button>
          ))}
        </section>
      )}
      <div
        role="group"
        aria-label={t('crmWorkspace.purpose')}
        className="grid gap-3 md:grid-cols-3"
      >
        {(['OPENING', 'REPLACEMENT', 'INCREMENT'] as const).map((value) => {
          const legacyBlocked = settings.data?.mode === 'REPLACEMENT' && value !== 'REPLACEMENT';
          return (
            <button
              type="button"
              className={`${OUTLINE} text-left flex-col items-start p-4`}
              key={value}
              aria-pressed={purpose === value}
              disabled={!contextReady || legacyBlocked || unresolved || workflow?.guard.busy}
              onClick={() => select(value)}
            >
              <strong>{t(`crmWorkspace.${value}`)}</strong>
              <span>{t(`crmWorkspace.${value}Hint`)}</span>
              <small>
                {t(legacyBlocked ? 'crmWorkspace.legacyBlocked' : 'crmWorkspace.previewRequired')}
              </small>
            </button>
          );
        })}
      </div>
      {purpose && (
        <>
          <ol aria-label={t('crmWorkspace.steps')} className="flex flex-wrap gap-3 text-sm">
            {phases.map((phase, i) => (
              <li
                key={phase}
                aria-current={model?.phase === phase ? 'step' : undefined}
                className={
                  model?.phase === phase ? 'font-bold text-primary' : 'text-base-content/70'
                }
              >
                {i + 1}. {t(`crmWorkspace.${phase}`)}
              </li>
            ))}
          </ol>
          {model?.phase === 'UNKNOWN' && (
            <p role="status" className={ALERT}>
              {t('crmWorkspace.unknown')}
            </p>
          )}
          {/* Existing adapters own DTOs, files, manual decisions, status and exact replay identity. */}
          {purpose === 'OPENING' ? (
            <TimedOpeningPanel entryVenueId={entryVenueId} />
          ) : purpose === 'INCREMENT' ? (
            <TimedIncrementPanel entryVenueId={entryVenueId} />
          ) : (
            <TimedReplacementPanel entryVenueId={entryVenueId} />
          )}
          {model && model.phase !== 'RESULT' && (
            <footer className="flex flex-wrap gap-3 border-t border-base-300 pt-4">
              <button
                type="button"
                className={OUTLINE}
                disabled={workflow?.guard.busy || unresolved}
                onClick={() => workflow?.commands.current?.back()}
              >
                {t('crmWorkspace.back')}
              </button>
              <button
                type="button"
                className={model.canConfirm ? OUTLINE : PRIMARY}
                disabled={!contextReady || !model.canPreview || workflow?.guard.busy || unresolved}
                onClick={() => workflow?.commands.current?.preview()}
              >
                {t('crmWorkspace.preview')}
              </button>
              <button
                type="button"
                className={PRIMARY}
                disabled={!contextReady || !model.canConfirm || workflow?.guard.busy || unresolved}
                onClick={() => workflow?.commands.current?.confirm()}
              >
                {t('crmWorkspace.confirm')}
              </button>
            </footer>
          )}
          {model?.phase === 'RESULT' && !unresolved && (
            <button
              type="button"
              className={OUTLINE}
              onClick={() => {
                if (workflow?.guard.busy) return;
                setPurpose(null);
                if (workflow) {
                  workflow.guard.dirty = false;
                  workflow.publish({ ...model, dirty: false });
                }
              }}
            >
              {t('crmWorkspace.new')}
            </button>
          )}
        </>
      )}
    </section>
  );
}
